import { CallObject, Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { genUUIDV4 } from '@frontend/string';
import { logError } from '../utils';

/**
 * Generates a new node and edge for a call route.
 *
 * @param {Object} params - The parameters for creating the new node and edge.
 * @param {NodeTypes} params.nodeType - The type of the node to be created.
 * @param {string} params.parentNodeId - The ID of the parent node.
 * @param {string} params.name - The name of the node.
 * @param {boolean} [params.isPhoneTreeDescendent=false] - Indicates if the node is a descendant of a phone tree.
 *
 * @returns {Object} An object containing the new node and edge.
 * @returns {Object.node} The newly created node.
 * @returns {Object.edge} The newly created edge.
 */
export const getNewNodeAndEdge = ({
  nodeType,
  parentNodeId,
  name,
  isPhoneTreeDescendent = false,
  callObject,
}: {
  nodeType: CallRouteV1.Types.NodeType;
  parentNodeId: string;
  name: string;
  isPhoneTreeDescendent?: boolean;
  callObject?: Partial<CallObject>;
}): {
  node: CallRouteV1.Types.Node;
  edge: Edge;
} => {
  const newNode = {
    id: genUUIDV4(),
    type: nodeType,
    callObject: {
      primitiveId: '',
      primitiveName: name,
      instructionId: '',
      instructionSetId: '',
      ...callObject,
    },
    isPhoneTreeDescendent,
  };

  const newEdge = {
    id: `${parentNodeId}:${newNode.id}`,
    sourceId: parentNodeId,
    targetId: newNode.id,
    type: 'basic',
  };

  if (!CallRouteV1.Types.isNodeOfCorrectTypeAndCallObject(newNode)) {
    logError('Invalid node type and call object configuration: PC-983udn20', 'getNewNodeAndEdge', {
      type: newNode.type,
      callObjectProperties: Object.keys(newNode.callObject),
    });
    throw new Error('Invalid node type and call object configuration: PC-983udn20');
  }

  return {
    node: newNode,
    edge: newEdge,
  };
};

/**
 * Sorts the edges of a graph in a depth-first order with specific requirements for PhoneTree and OfficeHours nodes.
 * For PhoneTree nodes, their direct children are sorted alphabetically by primitiveName, with Fallback nodes last.
 * For OfficeHours nodes, their direct children are sorted by type in the following order: OpenPhoneHours first,
 * ClosedPhoneHours second, any BreakPhoneHours alphabetically.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Node[]} params.nodes - The array of all nodes in the graph.
 * @param {Edge[]} params.edges - The array of all edges in the graph.
 * @returns {Object} - Object containing nodes and sorted edges.
 * @returns {Node[]} returns.nodes - The input nodes.
 * @returns {Edge[]} returns.edges - The edges sorted according to requirements.
 */
export const sortEdgesDepthFirst = ({
  nodes,
  edges,
}: {
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): { nodes: CallRouteV1.Types.Node[]; edges: Edge[] } => {
  // Create a map to track visited nodes
  const visited = new Set<string>();

  // Array to store sorted edges
  const sortedEdges: Edge[] = [];

  // Find the Start node
  const startNode = nodes.find((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_START);

  if (!startNode) {
    console.warn('No Start node found in the graph, returning original order');
    return { nodes, edges };
  }

  // Helper function to get node by ID
  const getNodeById = (id: string): CallRouteV1.Types.Node | undefined => {
    return nodes.find((node) => node.id === id);
  };

  // Helper function for depth-first traversal
  const dfs = (nodeId: string) => {
    // Mark node as visited
    visited.add(nodeId);

    const currentNode = getNodeById(nodeId);
    if (!currentNode) return;

    // Find all outgoing edges from this node
    let outgoingEdges = edges.filter((edge) => edge.sourceId === nodeId);

    // Apply special sorting for PhoneTree nodes
    if (currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE) {
      // Get target nodes to sort them
      const targetNodes = outgoingEdges
        .map((edge) => {
          const targetNode = getNodeById(edge.targetId);
          return { edge, targetNode };
        })
        .filter((item) => item.targetNode !== undefined) as { edge: Edge; targetNode: CallRouteV1.Types.Node }[];

      // Sort target nodes: Fallback nodes last, others alphabetically by primitiveName
      targetNodes.sort((a, b) => {
        // Fallback nodes always last
        if (
          a.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK &&
          b.targetNode.type !== CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK
        )
          return 1;
        if (
          a.targetNode.type !== CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK &&
          b.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK
        )
          return -1;

        // Sort alphabetically by primitiveName
        const aName = a.targetNode.callObject?.primitiveName || '';
        const bName = b.targetNode.callObject?.primitiveName || '';
        return aName.localeCompare(bName);
      });

      // Update outgoingEdges to reflect the sorting
      outgoingEdges = targetNodes.map((item) => item.edge);
    }
    // Apply special sorting for OfficeHours nodes
    else if (currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS) {
      // Get target nodes to sort them
      const targetNodes = outgoingEdges
        .map((edge) => {
          const targetNode = getNodeById(edge.targetId);
          return { edge, targetNode };
        })
        .filter((item) => item.targetNode !== undefined) as { edge: Edge; targetNode: CallRouteV1.Types.Node }[];

      // Sort target nodes: OpenPhoneHours first, ClosedPhoneHours second, then BreakPhoneHours alphabetically
      targetNodes.sort((a, b) => {
        // OpenPhoneHours always first
        if (
          a.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS &&
          b.targetNode.type !== CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS
        )
          return -1;
        if (
          a.targetNode.type !== CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS &&
          b.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS
        )
          return 1;

        // ClosedPhoneHours second
        if (
          a.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS &&
          b.targetNode.type !== CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS
        )
          return -1;
        if (
          a.targetNode.type !== CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS &&
          b.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS
        )
          return 1;

        // If both are BreakPhoneHours, sort alphabetically
        if (
          a.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS &&
          b.targetNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS
        ) {
          const aName = a.targetNode.callObject?.primitiveName || '';
          const bName = b.targetNode.callObject?.primitiveName || '';
          return aName.localeCompare(bName);
        }

        return 0;
      });

      // Update outgoingEdges to reflect the sorting
      outgoingEdges = targetNodes.map((item) => item.edge);
    }

    // Add these edges to our sorted result and continue traversal
    for (const edge of outgoingEdges) {
      sortedEdges.push(edge);

      // Continue depth-first if target hasn't been visited
      if (!visited.has(edge.targetId)) {
        dfs(edge.targetId);
      }
    }
  };

  // Start DFS from the Start node
  dfs(startNode.id);

  // Check if we've missed any edges (for disconnected components)
  const missedEdges = edges.filter(
    (edge) =>
      !sortedEdges.some((sortedEdge) => sortedEdge.sourceId === edge.sourceId && sortedEdge.targetId === edge.targetId)
  );

  // Append any missed edges to the end
  if (missedEdges.length > 0) {
    console.warn(`${missedEdges.length} edges were not reached in depth-first traversal starting from Start node`);
    sortedEdges.push(...missedEdges);
  }

  return { nodes, edges: sortedEdges };
};

/**
 * Compares two graphs for structural equality by performing a depth-first traversal.
 *
 * @param graph1 - The first graph to compare, containing nodes and edges.
 * @param graph2 - The second graph to compare, containing nodes and edges.
 * @returns `true` if the graphs are structurally equal, otherwise `false`.
 */
export const areGraphsEqual = (
  graph1: { nodes: CallRouteV1.Types.Node[]; edges: Edge[] },
  graph2: { nodes: CallRouteV1.Types.Node[]; edges: Edge[] }
) => {
  const { nodes: nodes1, edges: edges1 } = graph1;
  const { nodes: nodes2, edges: edges2 } = graph2;

  // If the number of nodes and edges are different, then the graphs are not equal.
  if (nodes1.length !== nodes2.length || edges1.length !== edges2.length) {
    return false;
  }

  // Create a map to track visited nodes
  const visited = new Set<string>();

  // Helper function to get node by ID
  const getNodeById = (id: string, nodes: CallRouteV1.Types.Node[]): CallRouteV1.Types.Node | undefined => {
    return nodes.find((node) => node.id === id);
  };

  // Helper function for depth-first traversal
  const dfs = (nodeId1: string, nodeId2: string) => {
    // Mark node as visited
    visited.add(nodeId1);

    const node1 = getNodeById(nodeId1, nodes1);
    const node2 = getNodeById(nodeId2, nodes2);

    if (!node1 || !node2) return false;

    // Compare the nodes
    if (node1.type !== node2.type) {
      return false;
    }

    if (node1.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS) {
      // Compare the phoneHoursExpansion objects
      const phoneHours1 = node1.callObject.phoneHoursExpansion?.phoneHours ?? [];
      // @ts-expect-error ignore this until we get better types
      const phoneHours2 = node2.callObject.phoneHoursExpansion?.phoneHours ?? [];

      if (phoneHours1.length !== phoneHours2.length) {
        return false;
      }

      for (let i = 0; i < phoneHours1.length; i++) {
        if (phoneHours1[i].scheduleId !== phoneHours2[i].scheduleId) {
          return false;
        }
      }
    } else if (node1.type === CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK) {
      // Fallback nodes are considered the same if they have the same type
      if (node1.type !== node2.type) {
        return false;
      }
    } else {
      // Compare the primitiveId
      if (node1.callObject.primitiveId !== node2.callObject.primitiveId) {
        return false;
      }
    }

    // Find all outgoing edges from this node
    const outgoingEdges1 = edges1.filter((edge) => edge.sourceId === nodeId1);
    const outgoingEdges2 = edges2.filter((edge) => edge.sourceId === nodeId2);

    // If the number of outgoing edges are different, then the graphs are not equal.
    if (outgoingEdges1.length !== outgoingEdges2.length) {
      return false;
    }

    // Continue traversal
    for (let i = 0; i < outgoingEdges1.length; i++) {
      const edge1 = outgoingEdges1[i];
      const edge2 = outgoingEdges2[i];

      const targetNode1 = getNodeById(edge1.targetId, nodes1);
      const targetNode2 = getNodeById(edge2.targetId, nodes2);

      // Compare the edges
      if (targetNode1?.callObject.primitiveId !== targetNode2?.callObject.primitiveId) {
        return false;
      }

      // Continue depth-first if target hasn't been visited
      if (!visited.has(edge1.targetId) && !dfs(edge1.targetId, edge2.targetId)) {
        return false;
      }
    }

    return true;
  };

  // Start DFS from the Start node
  return dfs(
    nodes1.find((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_START)?.id ?? '',
    nodes2.find((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_START)?.id ?? ''
  );
};
